import { Component, OnInit, ViewChild } from "@angular/core";
import {
  UntypedFormGroup,
  UntypedFormControl,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";
import { DateValidation } from "../../validators/dateValidation";
import { ToastrService } from "ngx-toastr";
import * as moment from "moment";
import {
  SPINNER_ANIMATIONS,
  SPINNER_PLACEMENT,
  ISpinnerConfig,
  NgxSpinner,
} from "@hardpool/ngx-spinner";
import { BaseChartDirective } from "ng2-charts";
import { KPILineChartDataset, KPIReport } from "../../model/report/kpiReport";
import { ExcelReportWriterHelper } from "../../helpers/excelReportWriterHelper";
import { LoggerService } from "../../services/logger.service";
import { ReportService } from "../../services/report.service";

@Component({
  selector: "historical",
  templateUrl: "historical.component.html",
  styleUrls: ["historical.component.css"],
})
export class HistoricalComponent implements OnInit {
  @ViewChild("spinner", { static: true }) spinner: NgxSpinner;
  @ViewChild(BaseChartDirective) myChart: any;

  searchForm: UntypedFormGroup;
  dateControl: UntypedFormControl;
  spinnerConfig: ISpinnerConfig;
  busy: boolean = false;
  noMatch: boolean = false;
  hasData: boolean = false;

  maxDate: Date = new Date();

  constructor(
    private fb: UntypedFormBuilder,
    private toaster: ToastrService,
    private readonly logger: LoggerService,
    private readonly reportService: ReportService
  ) {
    this.dateControl = new UntypedFormControl([Validators.required, DateValidation()]);
    this.searchForm = this.fb.group({
      date: this.dateControl,
    });
  }

  ngOnInit() {
    this.spinnerConfig = {
      placement: SPINNER_PLACEMENT.block_ui,
      animation: SPINNER_ANIMATIONS.horizontal_bounce,
      size: "2rem",
      color: "#7AB800",
      bgColor: "rgba(255,255,255,1)",
    };
  }

  public lineChartDataset: KPILineChartDataset[] = [
    { data: ([] = new Array<number>(144)), label: "Förare", hidden: false },
    { data: ([] = new Array<number>(144)), label: "Ankomster", hidden: false },
    { data: ([] = new Array<number>(144)), label: "Avgångar", hidden: false },
    { data: ([] = new Array<number>(144)), label: "Transfer", hidden: false },
    { data: ([] = new Array<number>(144)), label: "Rast", hidden: false },
    { data: ([] = new Array<number>(144)), label: "Bussbokningar", hidden: false },
  ];

  public lineChartLabels: Array<any> = new Array(144);
  public lineChartOptions = {
    responsive: true,
    elements: { point: { radius: 0, hitRadius: 10, hoverRadius: 10 } },
    tooltips: {
      callbacks: {
        title: function (tooltipItem) {
          let date: Date = new Date();
          date.setHours(0);
          date.setMinutes(0);
          date.setSeconds(0);
          return moment(date)
            .add(tooltipItem[0].index * 10, "m")
            .format("HH:mm");
        },

        afterBody: function (tooltipItems, data) {
          let label = "";
          data.datasets.forEach(dataset => {
            label += `${dataset.label}: ${dataset.data[tooltipItems[0].index]} st \r\n`;
          });
          return label;
        },

        label: function (tooltipItem, data ) {
          return "";
        }
      },
      backgroundColor: "#FFF",
      borderColor: "black",
      borderWidth: 1,
      cornerRadius: 2,
      titleFontSize: 16,
      titleFontColor: "#0066ff",
      bodyFontColor: "#000",
      bodyFontSize: 14,
      displayColors: false,
    },
    scales: {
      yAxes: [
        {
          scaleLabel: {
            display: true,
            labelString: "Antal",
            fontSize: 15,
          },
          ticks: {
            steps: 10,
            stepValue: 10,
            max: 60,
          },
        },
      ],
      xAxes: [
        {
          scaleLabel: {
            display: true,
            labelString: "Tid",
            fontSize: 15,
          },
          ticks: {
            maxRotation: 0,
            minRotation: 0,
            steps: 24,
            stepValue: 6,
            callback: function (label, index, labels) {
              const mod = index % 6;
              const div = index / 6;
              return mod == 0 ? div : null;
            },
          },
        },
      ],
    },
  };

  public lineChartColors = [
    {
      // Aktiva förare
      backgroundColor: "transparent",
      borderColor: "rgba(77,83,96,1)",
    },
    {
      // Airside Arrival
      backgroundColor: "transparent",
      borderColor: "#00B27F",
    },
    {
      // Airside Departure
      backgroundColor: "transparent",
      borderColor: "orange",
    },
    {
      // Transfer
      backgroundColor: "transparent",
      borderColor: "green",
    },
    {
      // Admin
      backgroundColor: "transparent",
      borderColor: "purple",
    },
    {
      // Bus bookings
      backgroundColor: "transparent",
      borderColor: "brown",
    }
  ];

  public lineChartLegend: boolean = true;
  public lineChartType: string = "line";

  canSearch() {
    return this.searchForm.valid && !this.busy;
  }

  canExport() {
    return !this.busy && !this.noMatch && this.hasData;
  }

  async export() {
    const date = new Date(this.dateControl.value);

    const result: KPIReport = await this.reportService.getKpiReport(
      date.toDateString(),
      true
    );

    ExcelReportWriterHelper.exportKpiReport(result, this.dateControl.value);
  }

  async search() {
    if (this.searchForm.valid) {
      this.spinner.show();
      const date = new Date(this.dateControl.value);
      this.busy = true;
      this.hasData = false;

      const labels: string[] = new Array(144);
      const drivers: number[] = new Array(144);
      const airsideArrivals: number[] = new Array(144);
      const airsideDepartures: number[] = new Array(144);
      const transfer: number[] = new Array(144);
      const admin: number[] = new Array(144);
      const busBookings: number[] = new Array(144);

      try {
        const result: KPIReport = await this.reportService.getKpiReport(
          date.toDateString()
        );
        this.spinner.hide();
        this.noMatch = false;

        let index = 0;
        result.assignments.forEach((assignment) => {
          labels[index] = "";
          drivers[index] = assignment.drivers;
          airsideArrivals[index] = assignment.airsideArrivals;
          airsideDepartures[index] = assignment.airsideDepartures;
          transfer[index] = assignment.transfers;
          admin[index] = assignment.admin;
          busBookings[index] = assignment.busBookings;
          index++;
        });

        this.lineChartLabels = labels;
        this.lineChartDataset[0].data = drivers;
        this.lineChartDataset[1].data = airsideArrivals;
        this.lineChartDataset[2].data = airsideDepartures;
        this.lineChartDataset[3].data = transfer;
        this.lineChartDataset[4].data = admin;
        this.lineChartDataset[5].data = busBookings;

        this.busy = false;
        this.hasData = true;
      } catch (error) {
        this.spinner.hide();
        this.noMatch = error.status == 404;
        this.busy = false;
        if (!this.noMatch) {
          let msg = "Ett fel uppstod vid inläsning av data."
          this.showError(msg);
          this.logger.logError(error, msg);
        } else {
          this.lineChartLabels = [];
          this.lineChartDataset.forEach((lcd) => (lcd.data = []));
        }
      }
    }
  }

  showError(message: string) {
    this.toaster.error(message);
  }
}
